import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { Modal } from "antd";
import { setTemporaryPassword } from "../redux/slices/UserSlice";

/**
 * Admin fallback for when onboarding email delivery fails.
 *
 * Resetting is destructive — it invalidates the business's current password and
 * ends their active sessions — so it always requires explicit confirmation.
 */
export const useResetTemporaryPassword = () => {
  const dispatch = useDispatch();

  return useCallback(
    (criteria) =>
      new Promise((resolve) => {
        Modal.confirm({
          title: "Reset temporary password?",
          content:
            "The current password stops working immediately and any active session is ended. The business must set a new password at their next login.",
          okText: "Reset and reveal",
          okButtonProps: { danger: true },
          cancelText: "Cancel",
          onCancel: () => resolve(false),
          onOk: async () => {
            const res = await dispatch(
              setTemporaryPassword({
                ...criteria,
                reveal: true,
                reason: "Onboarding email delivery failure",
              })
            );

            if (res.meta.requestStatus !== "fulfilled") {
              Modal.error({
                title: "Reset failed",
                content:
                  res.payload?.message ||
                  res.payload ||
                  res.error?.message ||
                  "Failed to set temporary password",
              });
              return resolve(false);
            }

            Modal.success({
              title: "Temporary password set",
              content: res.payload?.emailDelivered
                ? `Emailed to the business. Share over a secure channel only if they did not receive it: ${res.payload?.temporaryPassword}`
                : `Email delivery failed. Share this over a secure channel — it will not be shown again: ${res.payload?.temporaryPassword}`,
            });
            resolve(true);
          },
        });
      }),
    [dispatch]
  );
};
